import React, { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import { sbGetSetting, sbSetSetting } from "../utils/supabaseClient";
import { sendNotification } from "../utils/notifications";
import { useToast } from "./Toast";
import { parseMarkdown } from "../utils/markdownParser";
import PreviewModal from "./PreviewModal";
import PullToRefresh from "./PullToRefresh";
import { SVG_ICONS } from "./TablerIconPicker";
import "../styles/ListTab.css";

const PromptListTab = forwardRef(({ onHeaderInfoChange }, ref) => {
  const { success, error } = useToast();
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewContent, setPreviewContent] = useState(null);
  const [previewTitle, setPreviewTitle] = useState("");
  const [existingCategories, setExistingCategories] = useState([]);
  const [editingCategoryMode, setEditingCategoryMode] = useState("select");

  useEffect(() => {
    loadPrompts();
  }, []);

  useImperativeHandle(ref, () => ({
    loadPrompts
  }));

  useEffect(() => {
    onHeaderInfoChange?.({ title: "Prompts", count: prompts.length });
  }, [prompts, onHeaderInfoChange]);

  const loadPrompts = async () => {
    try {
      setLoading(true);
      const data = await sbGetSetting("origamic-prompts");
      const sorted = Array.isArray(data)
        ? [...data].sort((a, b) => new Date(b.addedAt) - new Date(a.addedAt))
        : [];
      setPrompts(sorted);
    } catch (err) {
      console.error("Error loading prompts:", err);
      error("Failed to load prompts");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this prompt?")) return;

    try {
      const updated = prompts.filter((p) => p.id !== id);
      await sbSetSetting("origamic-prompts", updated);
      setPrompts(updated);
      success("Prompt deleted");
    } catch (err) {
      console.error("Error deleting prompt:", err);
      error("Failed to delete prompt");
    }
  };

  const handleEdit = async (prompt) => {
    setEditingId(prompt.id);
    setEditFormData({ ...prompt });
    setEditingCategoryMode("select");

    try {
      const allPrompts = await sbGetSetting("origamic-prompts");
      if (Array.isArray(allPrompts)) {
        const categories = [...new Set(allPrompts.map(p => p.cat).filter(Boolean))].sort();
        setExistingCategories(categories);
      }
    } catch (err) {
      console.error("Error loading categories:", err);
    }
  };

  const handleSaveEdit = async () => {
    try {
      const oldPrompt = prompts.find((p) => p.id === editingId);
      const wasPublished = oldPrompt?.draft && !editFormData.draft;

      const updated = prompts.map((p) =>
        p.id === editingId ? editFormData : p
      );
      await sbSetSetting("origamic-prompts", updated);

      if (wasPublished) {
        await sendNotification(
          `💬 New Prompt • ${editFormData.title}`,
          editFormData.text.substring(0, 100) + "...",
          "prompts",
          editFormData.id
        );
      }

      setPrompts(updated);
      setEditingId(null);
      success("Prompt updated");
    } catch (err) {
      console.error("Error saving prompt:", err);
      error("Failed to save prompt");
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditFormData({});
    setEditingCategoryMode("select");
  };

  const handlePreview = (prompt) => {
    const content = (
      <div style={{ width: "100%", maxWidth: "100%" }}>
        <h1 style={{ color: "#5B9EF0", marginTop: 0 }}>{prompt.title}</h1>
        <div style={{ color: "#7A9CC4", fontSize: "14px", marginBottom: "20px" }}>
          {prompt.cat && <div>Category: {prompt.cat}</div>}
          {prompt.scenario && <div>Scenario: {prompt.scenario}</div>}
        </div>
        <div
          dangerouslySetInnerHTML={{ __html: parseMarkdown(prompt.text) }}
        />
      </div>
    );

    setPreviewTitle(`Prompt: ${prompt.title}`);
    setPreviewContent(content);
    setPreviewOpen(true);
  };

  const handleToggleDraft = async (prompt) => {
    try {
      const newDraftStatus = !prompt.draft;

      const updated = prompts.map(p =>
        p.id === prompt.id ? { ...p, draft: newDraftStatus } : p
      );
      await sbSetSetting("origamic-prompts", updated);

      if (newDraftStatus === false) {
        // Publishing: send notification
        await sendNotification(
          `💬 New Prompt • ${prompt.title}`,
          prompt.text.substring(0, 100) + "...",
          "prompts",
          prompt.id
        );
      }

      setPrompts(updated);
    } catch (err) {
      console.error("Error toggling draft status:", err);
      error("Failed to update draft status");
    }
  };

  if (loading) return <div className="loading">Loading prompts...</div>;

  if (prompts.length === 0)
    return <div className="empty-state">No prompts found. Create one first!</div>;

  // Get unique categories from prompts
  const uniqueCategories = [...new Set(prompts.map(prompt => prompt.cat).filter(Boolean))].sort();

  const filteredPrompts = prompts.filter((prompt) => {
    // Filter by search query
    const matchesSearch =
      prompt.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      prompt.cat?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      prompt.scenario?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      prompt.text?.toLowerCase().includes(searchQuery.toLowerCase());

    // Filter by selected category
    const matchesCategory = selectedCategory === null || prompt.cat === selectedCategory;

    // Filter by status
    const matchesStatus = selectedStatus === null || ((selectedStatus === "published" && !prompt.draft) || (selectedStatus === "draft" && prompt.draft));

    return matchesSearch && matchesCategory && matchesStatus;
  });

  return (
    <div className="list-container">
      <PreviewModal
        isOpen={previewOpen}
        onClose={() => setPreviewOpen(false)}
        content={previewContent}
        title={previewTitle}
      />

      <input
        type="text"
        placeholder="Search prompts..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        style={{
          width: "100%",
          padding: "10px 12px",
          marginBottom: "12px",
          background: "#050e1a",
          border: "1px solid rgba(59, 130, 246, 0.2)",
          borderRadius: "6px",
          color: "#fff",
          fontSize: "14px",
          boxSizing: "border-box",
        }}
      />

      {/* Category Filter Pills */}
      <div style={{ display: "flex", gap: "8px", marginBottom: "20px", flexWrap: "wrap" }}>
        <button
          onClick={() => setSelectedCategory(null)}
          style={{
            padding: "6px 14px",
            background: selectedCategory === null ? "rgba(59, 130, 246, 0.2)" : "rgba(59, 130, 246, 0.05)",
            color: selectedCategory === null ? "#5B9EF0" : "#7A9CC4",
            border: selectedCategory === null ? "1px solid rgba(59, 130, 246, 0.4)" : "1px solid rgba(59, 130, 246, 0.2)",
            borderRadius: "20px",
            cursor: "pointer",
            fontSize: "13px",
            fontWeight: "500",
            transition: "all 0.2s",
          }}
          onMouseEnter={(e) => {
            if (selectedCategory !== null) {
              e.target.style.background = "rgba(59, 130, 246, 0.1)";
              e.target.style.borderColor = "rgba(59, 130, 246, 0.3)";
            }
          }}
          onMouseLeave={(e) => {
            if (selectedCategory !== null) {
              e.target.style.background = "rgba(59, 130, 246, 0.05)";
              e.target.style.borderColor = "rgba(59, 130, 246, 0.2)";
            }
          }}
        >
          All
        </button>
        {uniqueCategories.map((category) => {
          const count = prompts.filter(prompt => prompt.cat === category).length;
          return (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              style={{
                padding: "6px 14px",
                background: selectedCategory === category ? "rgba(59, 130, 246, 0.2)" : "rgba(59, 130, 246, 0.05)",
                color: selectedCategory === category ? "#5B9EF0" : "#7A9CC4",
                border: selectedCategory === category ? "1px solid rgba(59, 130, 246, 0.4)" : "1px solid rgba(59, 130, 246, 0.2)",
                borderRadius: "20px",
                cursor: "pointer",
                fontSize: "13px",
                fontWeight: "500",
                transition: "all 0.2s",
              }}
              onMouseEnter={(e) => {
                if (selectedCategory !== category) {
                  e.target.style.background = "rgba(59, 130, 246, 0.1)";
                  e.target.style.borderColor = "rgba(59, 130, 246, 0.3)";
                }
              }}
              onMouseLeave={(e) => {
                if (selectedCategory !== category) {
                  e.target.style.background = "rgba(59, 130, 246, 0.05)";
                  e.target.style.borderColor = "rgba(59, 130, 246, 0.2)";
                }
              }}
            >
              {category} ({count})
            </button>
          );
        })}
      </div>

      {/* Status Filter Pills */}
      <div style={{ display: "flex", gap: "8px", marginBottom: "20px", flexWrap: "wrap" }}>
        <button
          onClick={() => setSelectedStatus(null)}
          style={{
            padding: "6px 14px",
            background: selectedStatus === null ? "rgba(59, 130, 246, 0.2)" : "rgba(59, 130, 246, 0.05)",
            color: selectedStatus === null ? "#5B9EF0" : "#7A9CC4",
            border: selectedStatus === null ? "1px solid rgba(59, 130, 246, 0.4)" : "1px solid rgba(59, 130, 246, 0.2)",
            borderRadius: "20px",
            cursor: "pointer",
            fontSize: "13px",
            fontWeight: "500",
            transition: "all 0.2s",
          }}
        >
          All
        </button>
        {["published", "draft"].map((status) => {
          const label = status.charAt(0).toUpperCase() + status.slice(1);
          return (
            <button
              key={status}
              onClick={() => setSelectedStatus(status)}
              style={{
                padding: "6px 14px",
                background: selectedStatus === status ? "rgba(59, 130, 246, 0.2)" : "rgba(59, 130, 246, 0.05)",
                color: selectedStatus === status ? "#5B9EF0" : "#7A9CC4",
                border: selectedStatus === status ? "1px solid rgba(59, 130, 246, 0.4)" : "1px solid rgba(59, 130, 246, 0.2)",
                borderRadius: "20px",
                cursor: "pointer",
                fontSize: "13px",
                fontWeight: "500",
                transition: "all 0.2s",
              }}
            >
              {label}
            </button>
          );
        })}
      </div>

      {editingId ? (
        // Edit Form
        <div className="edit-form">
          <h3>Edit Prompt</h3>
          <div className="form-group">
            <label>Title</label>
            <input
              type="text"
              value={editFormData.title || ""}
              onChange={(e) =>
                setEditFormData({ ...editFormData, title: e.target.value })
              }
            />
          </div>
          <div className="form-group">
            <label>Category</label>
            {editingCategoryMode === "select" ? (
              <select
                value={editFormData.cat || ""}
                onChange={(e) => {
                  if (e.target.value === "new") {
                    setEditingCategoryMode("add");
                  } else {
                    setEditFormData({ ...editFormData, cat: e.target.value });
                  }
                }}
              >
                <option value="">-- Select Category --</option>
                {existingCategories.map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
                <option value="new">+ Add New Category</option>
              </select>
            ) : (
              <>
                <input
                  type="text"
                  value={editFormData.cat || ""}
                  onChange={(e) =>
                    setEditFormData({ ...editFormData, cat: e.target.value })
                  }
                  placeholder="Enter new category name..."
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => {
                    setEditingCategoryMode("select");
                  }}
                  style={{
                    marginTop: "8px",
                    padding: "8px 12px",
                    background: "#7A9CC4",
                    color: "white",
                    border: "none",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontSize: "12px",
                  }}
                >
                  Back to Categories
                </button>
              </>
            )}
          </div>
          <div className="form-group">
            <label>Scenario</label>
            <textarea
              value={editFormData.scenario || ""}
              onChange={(e) =>
                setEditFormData({ ...editFormData, scenario: e.target.value })
              }
              rows="3"
              style={{
                width: "100%",
                padding: "12px",
                border: "1px solid rgba(59, 130, 246, 0.15)",
                borderRadius: "8px",
                background: "#050e1a",
                color: "white",
                fontFamily: "inherit",
                fontSize: "14px",
                boxSizing: "border-box",
              }}
            />
          </div>
          <div className="form-group">
            <label>Scheduled Date (Optional)</label>
            <input
              type="date"
              value={editFormData.scheduled ? new Date(editFormData.scheduled).toISOString().slice(0, 10) : ""}
              onChange={(e) =>
                setEditFormData({ ...editFormData, scheduled: e.target.value ? new Date(e.target.value).toISOString() : null })
              }
              title="Schedule when this prompt should be published"
              style={{ width: "100%" }}
            />
            <small style={{ color: "#7A9CC4", marginTop: "4px", display: "block" }}>
              Optional: Schedule this prompt to be published on a future date
            </small>
          </div>
          <div className="form-group">
            <label>Content (Markdown)</label>
            <textarea
              value={editFormData.text || ""}
              onChange={(e) =>
                setEditFormData({ ...editFormData, text: e.target.value })
              }
              rows="8"
              style={{
                width: "100%",
                padding: "12px",
                border: "1px solid rgba(59, 130, 246, 0.15)",
                borderRadius: "8px",
                background: "#050e1a",
                color: "white",
                fontFamily: "inherit",
                fontSize: "14px",
                boxSizing: "border-box",
              }}
            />

            {/* Markdown Formatting Guide */}
            <details style={{ marginTop: "20px", padding: "16px", background: "rgba(59, 130, 246, 0.05)", borderRadius: "8px", border: "1px solid rgba(59, 130, 246, 0.15)" }}>
              <summary style={{ cursor: "pointer", fontWeight: "600", color: "#5B9EF0", fontSize: "14px", userSelect: "none" }}>
                Markdown Formatting Guide
              </summary>
              <div style={{ marginTop: "12px", color: "#9DB4D4", fontSize: "13px", lineHeight: "1.6" }}>
                <h4 style={{ color: "#5B9EF0", marginTop: "12px", marginBottom: "8px" }}>Fence Types (Info Boxes)</h4>
                <ul style={{ marginLeft: "20px" }}>
                  <li><code>:::tip</code> - Green info box with 💡 icon</li>
                  <li><code>:::warning</code> - Orange warning box with ⚠️ icon</li>
                  <li><code>:::error</code> - Red error box with ❌ icon</li>
                  <li><code>:::info</code> - Blue info box with ℹ️ icon</li>
                  <li><code>:::button Label, URL</code> - Clickable button (orange)</li>
                </ul>

                <h4 style={{ color: "#5B9EF0", marginTop: "12px", marginBottom: "8px" }}>Basic Formatting</h4>
                <ul style={{ marginLeft: "20px" }}>
                  <li><code>**text**</code> - Bold</li>
                  <li><code>*text*</code> - Italic</li>
                  <li><code>`code`</code> - Inline code</li>
                  <li><code># Heading</code> - Large heading</li>
                  <li><code>- [ ]</code> - Checkbox (read-only)</li>
                  <li><code>[text](url)</code> - Link</li>
                </ul>

                <h4 style={{ color: "#5B9EF0", marginTop: "12px", marginBottom: "8px" }}>Examples</h4>
                <div style={{ background: "#050e1a", padding: "12px", borderRadius: "6px", overflow: "auto", marginTop: "8px", border: "1px solid rgba(59, 130, 246, 0.2)" }}>
                  <pre style={{ margin: "0", color: "#5B9EF0", fontSize: "12px" }}>
{`:::tip
This is a helpful tip
:::

:::button Get Started, https://example.com
:::

**Bold text** and *italic text*

- Bullet 1
- Bullet 2`}
                  </pre>
                </div>

                <p style={{ marginTop: "12px", color: "#7A9CC4", fontSize: "12px" }}>
                  📥 Download the full guide: <a href="/MARKDOWN_FORMATTING_GUIDE.md" style={{ color: "#5B9EF0", textDecoration: "underline" }}>MARKDOWN_FORMATTING_GUIDE.md</a>
                </p>
              </div>
            </details>
          </div>
          <div className="form-group">
            <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={!editFormData.draft}
                onChange={(e) =>
                  setEditFormData({ ...editFormData, draft: !e.target.checked })
                }
              />
              Published (unpublished prompts stay in draft and don't notify users)
            </label>
          </div>
          <div className="form-actions">
            <button className="btn-save" onClick={handleSaveEdit}>
              Save Changes
            </button>
            <button className="btn-cancel" onClick={handleCancel}>
              Cancel
            </button>
          </div>
        </div>
      ) : (
        // Prompts List
        <PullToRefresh onRefresh={loadPrompts}>
          {filteredPrompts.length === 0 && searchQuery && (
            <div className="empty-state">No prompts match your search</div>
          )}
          {filteredPrompts.length > 0 && (
          <div className="items-grid">
            {filteredPrompts.map((prompt) => (
            <div key={prompt.id} className="item-card">
              <div className="item-content">
                <h4>{prompt.title}</h4>
                {prompt.cat && <p className="item-meta">Category: {prompt.cat}</p>}
                {prompt.scenario && <p className="item-meta">Scenario: {prompt.scenario}</p>}
                <p className="item-preview">{prompt.text.substring(0, 100)}...</p>
                <div style={{display: "flex", gap: "8px", marginTop: "8px"}}>
                  {prompt.draft ? (
                    <div style={{display: "flex", alignItems: "center", gap: "4px", padding: "4px 12px", background: "rgba(245, 158, 11, 0.2)", color: "#F59E0B", borderRadius: "6px", fontSize: "12px", fontWeight: "600"}}>
                      {SVG_ICONS["alert-circle"]({ size: 14, color: "#F59E0B" })}
                      DRAFT
                    </div>
                  ) : (
                    <div style={{display: "flex", alignItems: "center", gap: "4px", padding: "4px 12px", background: "#22C55E20", color: "#22C55E", borderRadius: "6px", fontSize: "12px", fontWeight: "600"}}>
                      {SVG_ICONS["check-circle"]({ size: 14, color: "#22C55E" })}
                      PUBLISHED
                    </div>
                  )}
                </div>
              </div>
              <div className="item-actions">
                <button
                  onClick={() => handlePreview(prompt)}
                  title="Preview"
                  style={{
                    padding: "6px 12px",
                    background: "rgba(59, 130, 246, 0.1)",
                    color: "#5B9EF0",
                    border: "1px solid rgba(59, 130, 246, 0.2)",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontSize: "14px",
                    transition: "all 0.2s",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = "rgba(59, 130, 246, 0.2)";
                    e.target.style.borderColor = "rgba(59, 130, 246, 0.4)";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = "rgba(59, 130, 246, 0.1)";
                    e.target.style.borderColor = "rgba(59, 130, 246, 0.2)";
                  }}
                >
                  {SVG_ICONS["eye"]({ size: 18, color: "#5B9EF0" })}
                </button>
                <button
                  onClick={() => handleToggleDraft(prompt)}
                  title={prompt.draft ? "Publish prompt" : "Revert to draft"}
                  style={{
                    padding: "6px 12px",
                    background: prompt.draft ? "rgba(34, 197, 94, 0.1)" : "rgba(245, 158, 11, 0.1)",
                    color: prompt.draft ? "#22C55E" : "#F59E0B",
                    border: prompt.draft ? "1px solid rgba(34, 197, 94, 0.2)" : "1px solid rgba(245, 158, 11, 0.2)",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontSize: "14px",
                    transition: "all 0.2s",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "4px",
                  }}
                  onMouseEnter={(e) => {
                    if (prompt.draft) {
                      e.target.style.background = "rgba(34, 197, 94, 0.2)";
                      e.target.style.borderColor = "rgba(34, 197, 94, 0.4)";
                    } else {
                      e.target.style.background = "rgba(245, 158, 11, 0.2)";
                      e.target.style.borderColor = "rgba(245, 158, 11, 0.4)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (prompt.draft) {
                      e.target.style.background = "rgba(34, 197, 94, 0.1)";
                      e.target.style.borderColor = "rgba(34, 197, 94, 0.2)";
                    } else {
                      e.target.style.background = "rgba(245, 158, 11, 0.1)";
                      e.target.style.borderColor = "rgba(245, 158, 11, 0.2)";
                    }
                  }}
                >
                  {prompt.draft
                    ? SVG_ICONS["check"]({ size: 18, color: "#22C55E" })
                    : SVG_ICONS["circle-x"]({ size: 18, color: "#F59E0B" })}
                  <span style={{ fontSize: "12px" }}>
                    {prompt.draft ? "Publish" : "Draft"}
                  </span>
                </button>
                <button
                  onClick={() => handleEdit(prompt)}
                  style={{
                    padding: "6px 12px",
                    background: "rgba(59, 130, 246, 0.1)",
                    color: "#5B9EF0",
                    border: "1px solid rgba(59, 130, 246, 0.2)",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontSize: "14px",
                    transition: "all 0.2s",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = "rgba(59, 130, 246, 0.2)";
                    e.target.style.borderColor = "rgba(59, 130, 246, 0.4)";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = "rgba(59, 130, 246, 0.1)";
                    e.target.style.borderColor = "rgba(59, 130, 246, 0.2)";
                  }}
                >
                  {SVG_ICONS["pencil"]({ size: 18, color: "#5B9EF0" })}
                </button>
                <button
                  onClick={() => handleDelete(prompt.id)}
                  style={{
                    padding: "6px 12px",
                    background: "rgba(59, 130, 246, 0.1)",
                    color: "#5B9EF0",
                    border: "1px solid rgba(59, 130, 246, 0.2)",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontSize: "14px",
                    transition: "all 0.2s",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = "rgba(59, 130, 246, 0.2)";
                    e.target.style.borderColor = "rgba(59, 130, 246, 0.4)";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = "rgba(59, 130, 246, 0.1)";
                    e.target.style.borderColor = "rgba(59, 130, 246, 0.2)";
                  }}
                >
                  {SVG_ICONS["trash"]({ size: 18, color: "#5B9EF0" })}
                </button>
              </div>
            </div>
            ))}
          </div>
          )}
        </PullToRefresh>
      )}
    </div>
  );
});

PromptListTab.displayName = 'PromptListTab';

export default PromptListTab;
